import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Save,
  Eye,
  ArrowRight,
  ArrowLeft,
  ClipboardList,
  Check,
  Loader2,
  Plus,
  Trash2,
  BookOpen,
  X,
  SlidersHorizontal,
  Gauge,
  ListChecks,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { MultiSelect } from "../../components/ui/multi-select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import { useData } from "../../context/DataContext";
import api from "../../utils/api";
import { toast } from "sonner";

const STEPS = [
  { number: 1, label: "Basic Information", description: "Template name and type" },
  { number: 2, label: "Scope & Values", description: "Target roles and values" },
  { number: 3, label: "Question Sets", description: "Organize questions into sets" },
  { number: 4, label: "Scoring & Settings", description: "Configure scoring rules" },
  { number: 5, label: "Review & Save", description: "Final review and save" },
];

const TEMPLATE_TYPES = [
  { value: "VALUE_BASED", label: "Values-Based" },
  { value: "TECHNICAL", label: "Technical" },
  { value: "BEHAVIORAL", label: "Behavioral" },
  { value: "LEADERSHIP", label: "Leadership" },
  { value: "MIXED", label: "Mixed" },
];

const EXPERIENCE_LEVELS = [
  { value: "ALL", label: "All Levels" },
  { value: "ENTRY", label: "Entry Level" },
  { value: "MID", label: "Mid Level" },
  { value: "SENIOR", label: "Senior Level" },
  { value: "EXECUTIVE", label: "Executive" },
];

interface TargetValue {
  id: number;
  name: string;
  description: string | null;
}

interface InterviewQuestion {
  id: number;
  text: string;
  targetValues: string[];
  description: string | null;
}

interface QuestionSet {
  id: string;
  name: string;
  description: string;
  durationMinutes: number;
  questionIds: number[];
}

interface ScoringCriterion {
  id: string;
  name: string;
  weight: number;
}

interface ScoringConfig {
  scaleType: "1-5" | "1-10";
  scaleLabels: Record<string, string>;
  criteria: ScoringCriterion[];
  passingScore: number;
  requireFeedback: boolean;
  requireNotes: boolean;
}

const CreateInterviewTemplate: React.FC = () => {
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();
  const { id } = useParams();
  const { jobRoles, departments } = useData();
  const isEditMode = !!id;

  if (!isEditMode && !ability.can(ACTIONS.CREATE, RESOURCES.INTERVIEW_TEMPLATE)) {
    return <AccessRestricted />;
  }
  if (isEditMode && !ability.can(ACTIONS.UPDATE, RESOURCES.INTERVIEW_TEMPLATE)) {
    return <AccessRestricted />;
  }

  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  // Step 1 form state
  const [templateName, setTemplateName] = useState("");
  const [templateType, setTemplateType] = useState("VALUE_BASED");
  const [description, setDescription] = useState("");
  const [experienceLevel, setExperienceLevel] = useState("ALL");

  // Step 2 form state
  const [selectedJobRoleIds, setSelectedJobRoleIds] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [targetValues, setTargetValues] = useState<TargetValue[]>([]);
  const [selectedTargetValueIds, setSelectedTargetValueIds] = useState<number[]>([]);
  const [targetValuesLoading, setTargetValuesLoading] = useState(false);

  // Step 3 form state
  const [questionSets, setQuestionSets] = useState<QuestionSet[]>([]);
  const [showQuestionDialog, setShowQuestionDialog] = useState(false);
  const [activeQuestionSetId, setActiveQuestionSetId] = useState<string | null>(null);
  const [selectedQuestionIds, setSelectedQuestionIds] = useState<number[]>([]);

  // Interview questions from database
  const [interviewQuestions, setInterviewQuestions] = useState<InterviewQuestion[]>([]);
  const [interviewQuestionsLoading, setInterviewQuestionsLoading] = useState(false);

  // Step 4 form state — Scoring & Settings
  const [scoringConfig, setScoringConfig] = useState<ScoringConfig>({
    scaleType: "1-5",
    scaleLabels: {
      "1": "Poor",
      "2": "Below Average",
      "3": "Average",
      "4": "Good",
      "5": "Excellent",
    },
    criteria: [
      { id: crypto.randomUUID(), name: "Technical Skills", weight: 25 },
      { id: crypto.randomUUID(), name: "Communication", weight: 25 },
      { id: crypto.randomUUID(), name: "Culture Fit", weight: 25 },
      { id: crypto.randomUUID(), name: "Problem Solving", weight: 25 },
    ],
    passingScore: 3,
    requireFeedback: true,
    requireNotes: false,
  });

  // Fetch target values and interview questions on mount
  useEffect(() => {
    const fetchTargetValues = async () => {
      setTargetValuesLoading(true);
      try {
        const res = await api.get<{ success: boolean; values: TargetValue[] }>("/api/target-values");
        if (res.success) setTargetValues(res.values);
      } catch (err) {
        console.error("Failed to load target values:", err);
      } finally {
        setTargetValuesLoading(false);
      }
    };
    const fetchInterviewQuestions = async () => {
      setInterviewQuestionsLoading(true);
      try {
        const res = await api.get<{ success: boolean; questions: InterviewQuestion[] }>("/api/interview-questions");
        if (res.success) setInterviewQuestions(res.questions);
      } catch (err) {
        console.error("Failed to load interview questions:", err);
      } finally {
        setInterviewQuestionsLoading(false);
      }
    };
    fetchTargetValues();
    fetchInterviewQuestions();
  }, []);

  // Fetch template data in edit mode
  useEffect(() => {
    if (!isEditMode || !id) return;
    const fetchTemplate = async () => {
      setEditLoading(true);
      try {
        const res = await api.get<{ success: boolean; template: any }>(`/api/interview-templates/${id}`);
        if (res.success && res.template) {
          const t = res.template;
          setTemplateName(t.name || "");
          setTemplateType(t.type || "VALUE_BASED");
          setDescription(t.description || "");
          setExperienceLevel(t.level || "ALL");
          setSelectedJobRoleIds((t.jobRoles || []).map((jr: any) => String(jr.id)));
          setSelectedDepartmentIds((t.departments || []).map((d: any) => String(d.id)));
          setSelectedTargetValueIds((t.targetValues || []).map((tv: any) => tv.id));
          setQuestionSets(
            (t.questionSets || []).map((qs: any) => ({
              id: crypto.randomUUID(),
              name: qs.name || "",
              description: qs.description || "",
              durationMinutes: qs.durationMinutes || 15,
              questionIds: qs.questionIds || [],
            }))
          );
          if (t.scoringConfig) {
            setScoringConfig({
              scaleType: t.scoringConfig.scaleType || "1-5",
              scaleLabels: t.scoringConfig.scaleLabels || {
                "1": "Poor",
                "2": "Below Average",
                "3": "Average",
                "4": "Good",
                "5": "Excellent",
              },
              criteria: (t.scoringConfig.criteria || []).map((c: any) => ({
                id: crypto.randomUUID(),
                name: c.name || "",
                weight: c.weight || 0,
              })),
              passingScore: t.scoringConfig.passingScore || 3,
              requireFeedback: t.scoringConfig.requireFeedback !== false,
              requireNotes: t.scoringConfig.requireNotes === true,
            });
          }
        }
      } catch (err) {
        console.error("Failed to load template for editing:", err);
        toast.error("Failed to load template for editing");
        navigate("/recruitment/interview-templates");
      } finally {
        setEditLoading(false);
      }
    };
    fetchTemplate();
  }, [isEditMode, id]);

  const handleSave = async () => {
    if (!templateName.trim()) {
      toast.error("Template name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: templateName.trim(),
        type: templateType,
        level: experienceLevel,
        description: description.trim() || null,
        jobRoleIds: selectedJobRoleIds.map(Number),
        departmentIds: selectedDepartmentIds.map(Number),
        targetValueIds: selectedTargetValueIds,
        questionSets: questionSets.map((qs) => ({
          name: qs.name,
          description: qs.description,
          durationMinutes: qs.durationMinutes,
          questionIds: qs.questionIds,
        })),
        scoringConfig,
      };
      if (isEditMode && id) {
        const res = await api.put<{ success: boolean }>(`/api/interview-templates/${id}`, payload);
        if (res.success) {
          toast.success("Interview template updated successfully");
          navigate("/recruitment/interview-templates");
        }
      } else {
        const res = await api.post<{ success: boolean }>("/api/interview-templates", payload);
        if (res.success) {
          toast.success("Interview template created successfully");
          navigate("/recruitment/interview-templates");
        }
      }
    } catch (err: any) {
      toast.error(err.message || "Failed to create template");
    } finally {
      setSaving(false);
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length) {
      setCurrentStep((s) => s + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((s) => s - 1);
    }
  };

  const toggleTargetValue = (id: number) => {
    setSelectedTargetValueIds((prev) =>
      prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]
    );
  };

  // Question Sets helpers
  const addQuestionSet = () => {
    const newSet: QuestionSet = {
      id: crypto.randomUUID(),
      name: `Question Set ${questionSets.length + 1}`,
      description: "",
      durationMinutes: 15,
      questionIds: [],
    };
    setQuestionSets((prev) => [...prev, newSet]);
  };

  const updateQuestionSet = (id: string, updates: Partial<QuestionSet>) => {
    setQuestionSets((prev) =>
      prev.map((qs) => (qs.id === id ? { ...qs, ...updates } : qs))
    );
  };

  const removeQuestionSet = (id: string) => {
    setQuestionSets((prev) => prev.filter((qs) => qs.id !== id));
  };

  const openAddQuestionsDialog = (setId: string) => {
    const set = questionSets.find((qs) => qs.id === setId);
    setActiveQuestionSetId(setId);
    setSelectedQuestionIds(set?.questionIds || []);
    setShowQuestionDialog(true);
  };

  const applySelectedQuestions = () => {
    if (!activeQuestionSetId) return;
    updateQuestionSet(activeQuestionSetId, { questionIds: selectedQuestionIds });
    setShowQuestionDialog(false);
    setActiveQuestionSetId(null);
  };

  const toggleQuestionSelection = (questionId: number) => {
    setSelectedQuestionIds((prev) =>
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    );
  };

  // Scoring & Settings helpers
  const updateScoringConfig = (updates: Partial<ScoringConfig>) => {
    setScoringConfig((prev) => ({ ...prev, ...updates }));
  };

  const setScaleType = (type: "1-5" | "1-10") => {
    const defaults: Record<string, string> =
      type === "1-5"
        ? { "1": "Poor", "2": "Below Average", "3": "Average", "4": "Good", "5": "Excellent" }
        : { "1": "Poor", "2": "", "3": "", "4": "", "5": "Average", "6": "", "7": "", "8": "", "9": "", "10": "Excellent" };
    setScoringConfig((prev) => ({
      ...prev,
      scaleType: type,
      scaleLabels: defaults,
      passingScore: type === "1-5" ? 3 : 6,
    }));
  };

  const updateScaleLabel = (value: string, label: string) => {
    setScoringConfig((prev) => ({
      ...prev,
      scaleLabels: { ...prev.scaleLabels, [value]: label },
    }));
  };

  const addCriterion = () => {
    setScoringConfig((prev) => ({
      ...prev,
      criteria: [...prev.criteria, { id: crypto.randomUUID(), name: "", weight: 0 }],
    }));
  };

  const updateCriterion = (id: string, updates: Partial<ScoringCriterion>) => {
    setScoringConfig((prev) => ({
      ...prev,
      criteria: prev.criteria.map((c) => (c.id === id ? { ...c, ...updates } : c)),
    }));
  };

  const removeCriterion = (id: string) => {
    setScoringConfig((prev) => ({
      ...prev,
      criteria: prev.criteria.filter((c) => c.id !== id),
    }));
  };

  const totalWeight = scoringConfig.criteria.reduce((sum, c) => sum + c.weight, 0);

  // Options for MultiSelect
  const jobRoleOptions = jobRoles
    .filter((jr) => !jr.archived)
    .map((jr) => ({ value: String(jr.id), label: jr.title }));

  const departmentOptions = departments.map((d) => ({
    value: String(d.id),
    label: d.departmentName,
  }));

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10 px-0 sm:px-4 lg:px-0">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate("/recruitment/interview-templates")}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-700 transition-colors"
          >
            <ChevronLeft size={18} />
            <span className="text-sm font-medium">Back to Templates</span>
          </button>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-10 rounded-xl text-sm font-bold"
            onClick={() => {
              toast.info("Preview coming soon");
            }}
          >
            <Eye size={16} />
            <span>Preview</span>
          </Button>
          <Button
            className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-10 rounded-xl text-sm font-bold"
            onClick={handleSave}
            disabled={saving}
          >
            <Save size={16} />
            <span>{saving ? "Saving..." : (isEditMode ? "Update Template" : "Save Template")}</span>
          </Button>
        </div>
      </div>

      {/* Title */}
      <div className="flex items-center gap-3">
        <ClipboardList size={28} className="text-gray-900" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            {isEditMode ? "Edit Template" : "Create New Template"}
          </h1>
          <p className="text-gray-500 text-sm font-medium">
            {isEditMode ? "Update your interview template" : "Build a custom interview template"}
          </p>
        </div>
      </div>

      {editLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={28} className="animate-spin text-gray-400" />
          <span className="ml-3 text-sm text-gray-500 font-medium">Loading template...</span>
        </div>
      )}

      {/* Stepper */}
      <div className="bg-white p-4 sm:p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex items-start justify-between gap-2">
          {STEPS.map((step, index) => {
            const isActive = step.number === currentStep;
            const isCompleted = step.number < currentStep;
            const isLast = index === STEPS.length - 1;

            return (
              <div key={step.number} className="flex items-start gap-3 flex-1">
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${
                      isActive
                        ? "bg-blue-600 text-white"
                        : isCompleted
                        ? "bg-blue-100 text-blue-600"
                        : "bg-gray-100 text-gray-400"
                    }`}
                  >
                    {isCompleted ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      step.number
                    )}
                  </div>
                </div>
                <div className="hidden sm:block min-w-0">
                  <p
                    className={`text-sm font-bold ${
                      isActive || isCompleted ? "text-gray-900" : "text-gray-400"
                    }`}
                  >
                    {step.label}
                  </p>
                  <p className="text-xs text-gray-400 font-medium mt-0.5">
                    {step.description}
                  </p>
                </div>
                {!isLast && (
                  <div className="hidden lg:block flex-1 h-px bg-gray-100 mt-4 mx-2" />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      {currentStep === 1 && (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6">
          <div>
            <h4 className="font-bold text-gray-900 text-base">Basic Information</h4>
            <p className="text-gray-500 text-sm font-medium mt-0.5">
              Define the template name, type, and description
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                Template Name <span className="text-red-500">*</span>
              </label>
              <Input
                placeholder="e.g., Senior Developer Values Assessment"
                className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">
                Template Type <span className="text-red-500">*</span>
              </label>
              <Select value={templateType} onValueChange={setTemplateType}>
                <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-xl">
                  {TEMPLATE_TYPES.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <label className="text-sm font-bold text-gray-900">
              Description <span className="text-red-500">*</span>
            </label>
            <Textarea
              placeholder="Describe the purpose and focus of this interview template..."
              className="bg-gray-50 border-gray-200 rounded-xl text-sm font-medium focus:ring-2 focus:ring-blue-100 min-h-[100px] resize-y"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-1.5 md:max-w-md">
            <label className="text-sm font-bold text-gray-900">Experience Level</label>
            <Select value={experienceLevel} onValueChange={setExperienceLevel}>
              <SelectTrigger className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-medium focus:ring-2 focus:ring-blue-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl">
                {EXPERIENCE_LEVELS.map((l) => (
                  <SelectItem key={l.value} value={l.value}>
                    {l.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {currentStep === 2 && (
        <div className="space-y-6">
          {/* Target Roles & Departments */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Target Roles & Departments</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Select which roles and departments this template applies to
              </p>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Job Roles</label>
              <MultiSelect
                options={jobRoleOptions}
                selected={selectedJobRoleIds}
                onChange={setSelectedJobRoleIds}
                placeholder="Select job roles..."
                maxDisplayTags={2}
              />
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-bold text-gray-900">Departments</label>
              <MultiSelect
                options={departmentOptions}
                selected={selectedDepartmentIds}
                onChange={setSelectedDepartmentIds}
                placeholder="Select departments..."
                maxDisplayTags={2}
              />
            </div>
          </div>

          {/* Target Values */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6">
            <div>
              <h4 className="font-bold text-gray-900 text-base">
                Target Values <span className="text-red-500">*</span>
              </h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Select the core values this template will assess
              </p>
            </div>

            {targetValuesLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500 font-medium">Loading target values...</span>
              </div>
            ) : targetValues.length === 0 ? (
              <div className="p-6 text-center text-gray-400 text-sm font-medium bg-gray-50/50 rounded-xl border border-gray-100">
                No target values found. Add target values in Recruitment Settings.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {targetValues.map((tv) => {
                  const isSelected = selectedTargetValueIds.includes(tv.id);
                  return (
                    <button
                      key={tv.id}
                      onClick={() => toggleTargetValue(tv.id)}
                      className={`flex items-start gap-3 p-3 rounded-xl border text-left transition-all ${
                        isSelected
                          ? "border-blue-200 bg-blue-50/50"
                          : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                      }`}
                    >
                      <div
                        className={`w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                          isSelected
                            ? "bg-blue-600 border-blue-600"
                            : "bg-white border-gray-300"
                        }`}
                      >
                        {isSelected && <Check size={10} className="text-white" strokeWidth={3} />}
                      </div>
                      <div className="min-w-0">
                        <p className={`text-sm font-bold ${isSelected ? "text-blue-700" : "text-gray-900"}`}>
                          {tv.name}
                        </p>
                        {tv.description && (
                          <p className="text-xs text-gray-500 font-medium mt-0.5">
                            {tv.description}
                          </p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {currentStep === 3 && (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-bold text-gray-900 text-base">Question Sets</h4>
              <p className="text-gray-500 text-sm font-medium mt-0.5">
                Organize questions into logical groups for the interview
              </p>
            </div>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-10 rounded-xl text-sm font-bold px-4"
              onClick={addQuestionSet}
            >
              <Plus size={16} />
              <span>Add Question Set</span>
            </Button>
          </div>

          {questionSets.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 space-y-4">
              <BookOpen size={40} className="text-gray-300" />
              <div className="text-center">
                <p className="text-sm font-bold text-gray-900">No Question Sets</p>
                <p className="text-xs text-gray-500 font-medium mt-1">
                  Add question sets to organize your interview questions
                </p>
              </div>
              <Button
                className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-10 rounded-xl text-sm font-bold px-4"
                onClick={addQuestionSet}
              >
                <Plus size={16} />
                <span>Add Your First Question Set</span>
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {questionSets.map((qs) => (
                <div
                  key={qs.id}
                  className="p-4 rounded-xl border border-gray-100 space-y-4"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <Input
                      placeholder="Question Set Name"
                      className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-100 md:flex-1"
                      value={qs.name}
                      onChange={(e) => updateQuestionSet(qs.id, { name: e.target.value })}
                    />
                    <Input
                      placeholder="Description"
                      className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-medium focus:ring-2 focus:ring-blue-100 md:flex-1"
                      value={qs.description}
                      onChange={(e) => updateQuestionSet(qs.id, { description: e.target.value })}
                    />
                    <div className="flex items-center gap-2 shrink-0">
                      <Input
                        type="number"
                        min={1}
                        className="bg-gray-50 border-gray-200 rounded-xl h-10 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-100 w-20 text-center"
                        value={qs.durationMinutes}
                        onChange={(e) => updateQuestionSet(qs.id, { durationMinutes: parseInt(e.target.value) || 0 })}
                      />
                      <span className="text-xs text-gray-500 font-medium">min</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Button
                        variant="outline"
                        className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-10 rounded-xl text-xs font-bold px-3"
                        onClick={() => openAddQuestionsDialog(qs.id)}
                      >
                        <Plus size={14} />
                        <span>Add Questions</span>
                      </Button>
                      <button
                        onClick={() => removeQuestionSet(qs.id)}
                        className="text-gray-300 hover:text-red-500 transition-colors p-2"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-bold text-gray-900 mb-2">
                      Questions ({qs.questionIds.length}):
                    </p>
                    {qs.questionIds.length === 0 ? (
                      <p className="text-sm text-gray-400 font-medium italic">
                        No questions added yet
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {qs.questionIds.map((qid) => {
                          const q = interviewQuestions.find((lib) => lib.id === qid);
                          if (!q) return null;
                          return (
                            <div
                              key={qid}
                              className="p-3 rounded-xl bg-gray-50/50 border border-gray-100 text-sm"
                            >
                              <p className="font-bold text-gray-900">{q.text}</p>
                              <div className="flex flex-wrap gap-1.5 mt-1.5">
                                {q.targetValues?.map((tv) => (
                                  <span
                                    key={tv}
                                    className="inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold bg-purple-50 text-purple-700"
                                  >
                                    {tv}
                                  </span>
                                ))}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {currentStep === 4 && (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-8">
          <div>
            <h4 className="font-bold text-gray-900 text-base">Scoring & Settings</h4>
            <p className="text-gray-500 text-sm font-medium mt-0.5">
              Configure how interviews using this template will be scored and evaluated
            </p>
          </div>

          {/* Scoring Scale */}
          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <Gauge size={18} className="text-gray-700" />
              <h5 className="text-sm font-bold text-gray-900">Scoring Scale</h5>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setScaleType("1-5")}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-left transition-all ${
                  scoringConfig.scaleType === "1-5"
                    ? "border-blue-200 bg-blue-50/50"
                    : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                }`}
              >
                <span className={`text-lg font-bold ${scoringConfig.scaleType === "1-5" ? "text-blue-700" : "text-gray-900"}`}>1 – 5</span>
                <span className="text-xs text-gray-500 font-medium">Standard 5-point scale</span>
              </button>
              <button
                onClick={() => setScaleType("1-10")}
                className={`flex flex-col items-center gap-2 p-4 rounded-xl border text-left transition-all ${
                  scoringConfig.scaleType === "1-10"
                    ? "border-blue-200 bg-blue-50/50"
                    : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                }`}
              >
                <span className={`text-lg font-bold ${scoringConfig.scaleType === "1-10" ? "text-blue-700" : "text-gray-900"}`}>1 – 10</span>
                <span className="text-xs text-gray-500 font-medium">Granular 10-point scale</span>
              </button>
            </div>

            {/* Scale Labels */}
            <div className="space-y-2">
              <p className="text-xs text-gray-500 font-bold uppercase tracking-wider">Scale Labels</p>
              <div className="grid grid-cols-5 gap-2">
                {Array.from({ length: scoringConfig.scaleType === "1-5" ? 5 : 10 }, (_, i) => {
                  const value = String(i + 1);
                  return (
                    <div key={value} className="space-y-1">
                      <label className="text-xs font-bold text-gray-500 block text-center">{value}</label>
                      <Input
                        className="bg-gray-50 border-gray-200 rounded-xl h-9 text-xs font-medium text-center focus:ring-2 focus:ring-blue-100 px-2"
                        value={scoringConfig.scaleLabels[value] || ""}
                        onChange={(e) => updateScaleLabel(value, e.target.value)}
                        placeholder="Label"
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Evaluation Criteria */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ListChecks size={18} className="text-gray-700" />
                <h5 className="text-sm font-bold text-gray-900">Evaluation Criteria</h5>
              </div>
              <Button
                variant="outline"
                className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-1.5 h-8 rounded-xl text-xs font-bold px-3"
                onClick={addCriterion}
              >
                <Plus size={14} />
                Add Criterion
              </Button>
            </div>

            {scoringConfig.criteria.length === 0 ? (
              <div className="text-center py-6 bg-gray-50/50 rounded-xl border border-gray-100">
                <p className="text-sm text-gray-400 font-medium">No criteria defined. Add at least one evaluation criterion.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {scoringConfig.criteria.map((criterion) => (
                  <div key={criterion.id} className="flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/30">
                    <div className="flex-1 min-w-0">
                      <Input
                        placeholder="Criterion name"
                        className="bg-white border-gray-200 rounded-xl h-9 text-sm font-bold text-gray-900 focus:ring-2 focus:ring-blue-100"
                        value={criterion.name}
                        onChange={(e) => updateCriterion(criterion.id, { name: e.target.value })}
                      />
                    </div>
                    <div className="flex items-center gap-2 shrink-0 w-40">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={criterion.weight}
                        onChange={(e) => updateCriterion(criterion.id, { weight: parseInt(e.target.value) })}
                        className="flex-1 accent-blue-600 h-1.5"
                      />
                      <span className="text-xs font-bold text-gray-700 w-10 text-right">{criterion.weight}%</span>
                    </div>
                    <button
                      onClick={() => removeCriterion(criterion.id)}
                      className="text-gray-300 hover:text-red-500 transition-colors p-1 shrink-0"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between px-1">
                  <span className="text-xs font-bold text-gray-500">Total Weight</span>
                  <span className={`text-xs font-bold ${totalWeight === 100 ? "text-green-600" : "text-amber-600"}`}>
                    {totalWeight}% {totalWeight !== 100 && "(should be 100%)"}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Passing Threshold & Interview Settings */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <SlidersHorizontal size={18} className="text-gray-700" />
                <h5 className="text-sm font-bold text-gray-900">Passing Threshold</h5>
              </div>
              <div className="flex items-center gap-3">
                <Input
                  type="number"
                  min={1}
                  max={scoringConfig.scaleType === "1-5" ? 5 : 10}
                  className="bg-gray-50 border-gray-200 rounded-xl h-11 text-sm font-bold text-gray-900 w-24 text-center focus:ring-2 focus:ring-blue-100"
                  value={scoringConfig.passingScore}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    const max = scoringConfig.scaleType === "1-5" ? 5 : 10;
                    updateScoringConfig({ passingScore: Math.max(1, Math.min(val, max)) });
                  }}
                />
                <span className="text-sm text-gray-500 font-medium">
                  out of {scoringConfig.scaleType === "1-5" ? 5 : 10}
                </span>
              </div>
              <p className="text-xs text-gray-400 font-medium">
                Minimum overall score a candidate must achieve to pass the interview.
              </p>
            </div>

            <div className="space-y-3">
              <h5 className="text-sm font-bold text-gray-900">Interview Settings</h5>
              <div className="space-y-2">
                <button
                  onClick={() => updateScoringConfig({ requireFeedback: !scoringConfig.requireFeedback })}
                  className="w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all hover:bg-gray-50/50 border-gray-100"
                >
                  <div
                    className={`w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                      scoringConfig.requireFeedback ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
                    }`}
                  >
                    {scoringConfig.requireFeedback && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Require Written Feedback</p>
                    <p className="text-xs text-gray-500 font-medium">Interviewers must provide written feedback before submitting scores.</p>
                  </div>
                </button>
                <button
                  onClick={() => updateScoringConfig({ requireNotes: !scoringConfig.requireNotes })}
                  className="w-full flex items-start gap-3 p-3 rounded-xl border text-left transition-all hover:bg-gray-50/50 border-gray-100"
                >
                  <div
                    className={`w-4 h-4 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                      scoringConfig.requireNotes ? "bg-blue-600 border-blue-600" : "bg-white border-gray-300"
                    }`}
                  >
                    {scoringConfig.requireNotes && <Check size={10} className="text-white" strokeWidth={3} />}
                  </div>
                  <div>
                    <p className="text-sm font-bold text-gray-900">Require Interviewer Notes</p>
                    <p className="text-xs text-gray-500 font-medium">Interviewers must take notes during the interview session.</p>
                  </div>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {currentStep === 5 && (
        <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6">
          <div>
            <h4 className="font-bold text-gray-900 text-base">Review & Save</h4>
            <p className="text-gray-500 text-sm font-medium mt-0.5">
              Review your template before saving
            </p>
          </div>

          <div className="space-y-4">
            <div className="p-4 rounded-xl bg-gray-50/50 border border-gray-100 space-y-3">
              <h5 className="text-sm font-bold text-gray-900">Basic Information</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Template Name</span>
                  <span className="font-bold text-gray-900">{templateName || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Template Type</span>
                  <span className="font-bold text-gray-900">
                    {TEMPLATE_TYPES.find((t) => t.value === templateType)?.label || templateType}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Description</span>
                  <span className="font-bold text-gray-900">{description || "—"}</span>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Experience Level</span>
                  <span className="font-bold text-gray-900">
                    {EXPERIENCE_LEVELS.find((l) => l.value === experienceLevel)?.label || experienceLevel}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-gray-50/50 border border-gray-100 space-y-3">
              <h5 className="text-sm font-bold text-gray-900">Scope & Values</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Job Roles</span>
                  <span className="font-bold text-gray-900">
                    {selectedJobRoleIds.length > 0
                      ? selectedJobRoleIds
                          .map((id) => jobRoleOptions.find((o) => o.value === id)?.label)
                          .filter(Boolean)
                          .join(", ")
                      : "—"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Departments</span>
                  <span className="font-bold text-gray-900">
                    {selectedDepartmentIds.length > 0
                      ? selectedDepartmentIds
                          .map((id) => departmentOptions.find((o) => o.value === id)?.label)
                          .filter(Boolean)
                          .join(", ")
                      : "—"}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Target Values</span>
                  <span className="font-bold text-gray-900">
                    {selectedTargetValueIds.length > 0
                      ? selectedTargetValueIds
                          .map((id) => targetValues.find((tv) => tv.id === id)?.name)
                          .filter(Boolean)
                          .join(", ")
                      : "—"}
                  </span>
                </div>
              </div>
            </div>

            {questionSets.length > 0 && (
              <div className="p-4 rounded-xl bg-gray-50/50 border border-gray-100 space-y-3">
                <h5 className="text-sm font-bold text-gray-900">Question Sets</h5>
                <div className="space-y-2">
                  {questionSets.map((qs) => (
                    <div key={qs.id} className="text-sm">
                      <p className="font-bold text-gray-900">
                        {qs.name} <span className="text-gray-400 font-medium">({qs.durationMinutes} min)</span>
                      </p>
                      <p className="text-xs text-gray-500 font-medium">
                        {qs.questionIds.length} question{qs.questionIds.length !== 1 ? "s" : ""}
                        {qs.description ? ` · ${qs.description}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="p-4 rounded-xl bg-gray-50/50 border border-gray-100 space-y-3">
              <h5 className="text-sm font-bold text-gray-900">Scoring & Settings</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Scoring Scale</span>
                  <span className="font-bold text-gray-900">{scoringConfig.scaleType}</span>
                </div>
                <div>
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Passing Threshold</span>
                  <span className="font-bold text-gray-900">{scoringConfig.passingScore} / {scoringConfig.scaleType === "1-5" ? 5 : 10}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Evaluation Criteria</span>
                  <span className="font-bold text-gray-900">
                    {scoringConfig.criteria.length > 0
                      ? scoringConfig.criteria.map((c) => `${c.name} (${c.weight}%)`).join(", ")
                      : "—"}
                  </span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-400 font-bold uppercase tracking-widest text-[10px] block">Interview Settings</span>
                  <span className="font-bold text-gray-900">
                    {[
                      scoringConfig.requireFeedback && "Feedback required",
                      scoringConfig.requireNotes && "Notes required",
                    ].filter(Boolean).join(" · ") || "No extra requirements"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Questions to Set Dialog */}
      <Dialog open={showQuestionDialog} onOpenChange={setShowQuestionDialog}>
        <DialogContent className="sm:max-w-[560px] p-0 gap-0 overflow-hidden rounded-2xl border border-gray-200">
          <DialogHeader className="p-6 pb-4">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-lg font-bold text-gray-900">
                Add Questions to Set
              </DialogTitle>
            </div>
            <p className="text-sm text-gray-500 font-medium mt-0.5">
              Select questions from the library to add to this question set
            </p>
          </DialogHeader>
          <div className="px-6 pb-6 space-y-4 max-h-[60vh] overflow-y-auto">
            {interviewQuestionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 size={20} className="animate-spin text-gray-400" />
                <span className="ml-2 text-sm text-gray-500 font-medium">Loading questions...</span>
              </div>
            ) : interviewQuestions.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen size={32} className="mx-auto text-gray-300 mb-2" />
                <p className="text-sm font-medium text-gray-500">No interview questions found.</p>
                <p className="text-xs text-gray-400 font-medium mt-1">Add questions in Recruitment Settings &rarr; Interview Questions.</p>
              </div>
            ) : (
              interviewQuestions.map((q) => {
                const isSelected = selectedQuestionIds.includes(q.id);
                return (
                  <button
                    key={q.id}
                    onClick={() => toggleQuestionSelection(q.id)}
                    className={`w-full flex items-start gap-3 p-4 rounded-xl border text-left transition-all ${
                      isSelected
                        ? "border-blue-200 bg-blue-50/50"
                        : "border-gray-100 hover:border-gray-200 hover:bg-gray-50/50"
                    }`}
                  >
                    <div
                      className={`w-5 h-5 rounded border shrink-0 mt-0.5 flex items-center justify-center transition-colors ${
                        isSelected
                          ? "bg-blue-600 border-blue-600"
                          : "bg-white border-gray-300"
                      }`}
                    >
                      {isSelected && <Check size={12} className="text-white" strokeWidth={3} />}
                    </div>
                    <div className="min-w-0 space-y-1.5">
                      <p className={`text-sm font-bold ${isSelected ? "text-blue-700" : "text-gray-900"}`}>
                        {q.text}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {q.targetValues?.map((tv) => (
                          <span
                            key={tv}
                            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ${
                              isSelected
                                ? "bg-blue-100 text-blue-700"
                                : "bg-purple-50 text-purple-700"
                            }`}
                          >
                            {tv}
                          </span>
                        ))}
                      </div>
                      {q.description && (
                        <p className="text-xs text-gray-500 font-medium">
                          {q.description}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })
            )}
          </div>
          <div className="px-6 pb-6 flex items-center justify-end gap-3 pt-2">
            <Button
              variant="outline"
              className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 h-10 rounded-xl text-sm font-bold px-5"
              onClick={() => setShowQuestionDialog(false)}
            >
              Cancel
            </Button>
            <Button
              className="bg-slate-950 hover:bg-slate-900 text-white h-10 rounded-xl text-sm font-bold px-5"
              onClick={applySelectedQuestions}
            >
              Add Selected Questions
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Navigation */}
      <div className="flex items-center justify-between pt-2">
        <Button
          variant="outline"
          className="bg-white border-gray-200 text-gray-700 hover:bg-gray-50 flex items-center gap-2 h-11 rounded-xl text-sm font-bold px-5"
          onClick={handlePrevious}
          disabled={currentStep === 1}
        >
          <ArrowLeft size={16} />
          <span>Previous</span>
        </Button>
        <Button
          className="bg-slate-950 hover:bg-slate-900 text-white flex items-center gap-2 h-11 rounded-xl text-sm font-bold px-5"
          onClick={currentStep === STEPS.length ? handleSave : handleNext}
          disabled={saving}
        >
          <span>{currentStep === STEPS.length ? (saving ? "Saving..." : "Save Template") : "Next"}</span>
          {currentStep !== STEPS.length && <ArrowRight size={16} />}
        </Button>
      </div>
    </div>
  );
};

export default CreateInterviewTemplate;
